import React from "react";
import { TrackingObject } from "@themuse/design-system/lib/types/TrackingTypes";

import styles from "./JobQuizContainer.module.scss";

interface ButtonOption {
    id?: number;
    display?: string;
    value: string;
}

interface ButtonGroupProps {
    choices: ButtonOption[];
    field?: string;
    flow?: boolean;
    onSelect?: (value: string[]) => void;
    clickTrackingData?: TrackingObject;
}

/**
 * Renders a Multiple select options group.
 */
const MultiSelectButtonGroup = (props: ButtonGroupProps) => {
    const {
        choices,
        field,
        flow,
        onSelect,
        clickTrackingData
    } = props;

    const [selectedItems, setSelectedItems] = React.useState<string[]>([]);
    const containerClasses = `${styles.buttonGroup} ${flow ? styles.buttonGroup__flow : ""}`;
    const inputClasses = `${styles.buttonGroup__item} ${styles.multiSelectItem}`;

    const handleChange = ($evt: any) => {
        const { value, checked } = $evt.target;
        $evt.target.blur();

        let newSelections: string[] = Object.assign([], selectedItems);
        if (checked && !selectedItems.includes(value)) {
            newSelections.push(value);
        }
        if (!checked && selectedItems.includes(value)) {
            newSelections = selectedItems.filter((item: string) => item !== value);
        }

        setSelectedItems(newSelections);
        onSelect?.(newSelections);
    };

    return (
        <div className={containerClasses}>
            {choices.map((option) => {
                const tracking: TrackingObject = { ...clickTrackingData };
                tracking.sp_quiz_answer = option.value;
                tracking.sp_click_button_text = option.display ? option.display : option.value;

                return (
                    // eslint-disable-next-line jsx-a11y/label-has-associated-control
                    <label className={inputClasses} key={option.value}>
                        <input
                            type="checkbox"
                            name={field ?? "multiselect-button-group"}
                            value={option.value}
                            onChange={handleChange}
                            data-tracking-props={JSON.stringify(tracking)}
                            data-tracking-event-type="click"
                        />
                        <p className={styles.inputTitle}>{option.display ? option.display : option.value}</p>
                    </label>
                );
            })}
        </div>
    );
};

/**
 * Renders a group of radio buttons as a button group.
 */
const ButtonGroup = (props: ButtonGroupProps) => {
    const {
        choices,
        field,
        flow,
        clickTrackingData,
        onSelect
    } = props;
    const [, setSelectedItem] = React.useState<string>("");
    const containerClasses = `${styles.buttonGroup} ${flow ? styles.buttonGroup__flow : ""}`;

    const handleChange = ($evt: any) => {
        const { value } = $evt.target;
        setSelectedItem(value);
        onSelect?.(value);
    };

    return (
        <div className={containerClasses}>
            {choices.map((option) => {
                const tracking: TrackingObject = { ...clickTrackingData };
                tracking.sp_quiz_answer = option.value;
                tracking.sp_click_button_text = option.display ? option.display : option.value;

                return (
                    // eslint-disable-next-line jsx-a11y/label-has-associated-control
                    <label
                        className={styles.buttonGroup__item}
                        key={option.value}
                    >
                        <input
                            type="radio"
                            name={field ?? "button-group"}
                            value={option.value}
                            onChange={handleChange}
                            data-tracking-props={JSON.stringify(tracking)}
                            data-tracking-event-type="click"
                        />
                        <p className={styles.inputTitle}>{option.display ? option.display : option.value}</p>
                    </label>
                );
            })}
        </div>
    );
};
ButtonGroup.displayName = "ButtonGroup";

export {
    ButtonGroup,
    MultiSelectButtonGroup
};
export type { ButtonGroupProps };
